<template>
  <div>
    <div class="d-flex justify-center mt-4">
      <v-btn
        v-for="role in roles"
        :key="role.id"
        @click="selectRole(role.id)"
        elevation="0"
        :ripple="false"
        height="43"
        class="font-weight-bold text-capitalize mx-2"
        :class="{
          'btn-primary bg-gradient-warning': selectedRoleId === role.id,
          'btn-secondary bg-gradient-light': selectedRoleId !== role.id,
        }"
      >
        {{ ROLE_MAPPING[role.name] || role.name }}
      </v-btn>
    </div>
    <v-simple-table v-if="viewMode === 'list'" class="table">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">權限名稱</th>
            <th class="text-left">描述</th>
            <th class="text-left">Resource</th>
            <th class="text-left">動作</th>
            <th class="text-center">啟用</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in all_permissions"
            :key="item.id"
            :class="{ 'loading-row': loading }"
          >
            <td>
              <div>{{ item.name }}</div>
            </td>
            <td>{{ item.description }}</td>
            <td>{{ item.resource }}</td>
            <td>{{ item.action }}</td>
            <td class="text-center">
              <v-checkbox
                v-model="item.enabled"
                @change="keepChanges(item)"
                hide-details
                class="ma-0 pa-0"
              ></v-checkbox>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <!-- Info Dialog for permission update confirmation -->
    <info-dialog
      :visible.sync="showInfoDialog"
      title="權限更新"
      message="權限已更新成功"
      yes-button-text="確定"
      :yes-callback="onDialogConfirm"
    />
    <progress-dialog :visible="showProgressDialog" :message="progressMessage" />
  </div>
</template>

<script>
import InfoDialog from "@/components/Common/InfoDialog.vue";
import ProgressDialog from "@/components/Common/ProgressDialog.vue";
import { ROLE_MAPPING } from "@/constants";

export default {
  name: "RolePermissions",
  components: {
    InfoDialog,
    ProgressDialog,
  },
  data() {
    return {
      showInfoDialog: false,
      showProgressDialog: false,
      progressMessage: "",
      ROLE_MAPPING,
      viewMode: "list",
      loading: false,
      selectedRoleId: 1,
      roles: [
        { id: 1, name: "consultant" },
        { id: 2, name: "hr" },
        { id: 3, name: "manager" },
        { id: 4, name: "employee" },
      ],
      role_permissions: [],
      all_permissions: [],
      change_permissions: [],
      headers: [
        {
          text: "權限名稱",
          value: "name",
          align: "start",
          sortable: true,
        },
        //description
        {
          text: "描述",
          value: "description",
          align: "start",
          sortable: true,
        },
        {
          text: "Resource",
          value: "resource",
          align: "start",
          sortable: true,
        },
        {
          text: "動作",
          value: "action",
          align: "start",
          sortable: true,
        },
        {
          text: "啟用",
          value: "enabled",
          align: "start",
          sortable: false,
        },
      ],
    };
  },

  mounted() {
    this.fetchAllPermissions().then(() => {
      this.fetchRolePermissions(this.selectedRoleId);
    });
  },

  methods: {
    selectRole(roleId) {
      this.showProgressDialog = true;
      this.progressMessage = "載入權限中...";
      this.selectedRoleId = roleId;
      this.fetchRolePermissions(roleId).then(() => {
        setTimeout(() => {
          this.showProgressDialog = false;
        }, 500);
      });
    },

    async fetchAllPermissions() {
      if (this.all_permissions.length > 0) {
        // Skip if already loaded
        return;
      }

      this.loading = true;
      try {
        const response = await this.$axios.get("/api/permissions");
        this.all_permissions = response.data.data.rows;
        console.log(" all_permissions.length", this.all_permissions.length);
        return this.all_permissions;
      } catch (error) {
        console.error("Error fetching all permissions:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchRolePermissions(roleId) {
      this.loading = true;
      // console.log("fetch Role", roleId);
      try {
        // Get the role permissions
        const response = await this.$axios.get(`/api/roles/${roleId}`);
        const rolePermissions = response.data.data.Permissions;

        // If we have all permissions loaded, compare and set enabled status
        if (this.all_permissions.length > 0) {
          // Create a map of role permission IDs for quick lookup
          const rolePermissionIds = new Set(rolePermissions.map((p) => p.id));
          // console.log("rolePermissionIds", rolePermissionIds);

          // Map all permissions and set enabled based on whether they exist in role permissions
          this.all_permissions.map((permission) => {
            permission.enabled = rolePermissionIds.has(permission.id);
          });
        } else {
          // Fallback to original behavior if all_permissions isn't loaded yet
          this.role_permissions = rolePermissions.map((permission) => ({
            ...permission,
            enabled: true,
          }));
        }
      } catch (error) {
        console.error("Error fetching role permissions:", error);
      } finally {
        this.loading = false;
      }
    },
    async keepChanges(permission) {
      // Here you would implement the API call to update the permission status

      const index = this.change_permissions.findIndex(
        (p) => p.id === permission.id
      );
      if (index !== -1) {
        this.change_permissions.splice(index, 1);
      }
      this.change_permissions.push(permission);
    },
    async applyChanges() {
      console.log("applyChanges--> ", this.selectedRoleId);
      this.loading = true;
      try {
        // get all enable = True
        const enabledPermissions = this.change_permissions.filter(
          (p) => p.enabled
        );
        console.log("enabledPermissions", enabledPermissions);

        // get all enabled = False
        const disabledPermissions = this.change_permissions.filter(
          (p) => !p.enabled
        );
        console.log("disabledPermissions", disabledPermissions);

        // If there are enabled permissions, assign them
        if (enabledPermissions.length > 0) {
          const permissionIds = enabledPermissions.map((p) => p.id);

          // Call the API to assign multiple permissions
          const assignResponse = await this.$axios.post(
            "/api/role-permissions/assign-multiple",
            {
              roleId: this.selectedRoleId,
              permissionIds: permissionIds,
            }
          );

          console.log("Permissions assigned:", assignResponse.data);
        }

        // If there are disabled permissions, remove them
        if (disabledPermissions.length > 0) {
          const permissionIds = disabledPermissions.map((p) => p.id);
          const ids_str = permissionIds.join(",");
          const action = `/api/role-permissions/role/${this.selectedRoleId}/permissions/${ids_str}`;

          // Call the API to remove multiple permissions

          const removeResponse = await this.$axios.delete(action, {
            data: {
              roleId: this.selectedRoleId,
              permissionIds: permissionIds,
            },
          });

          console.log("Permissions removed:", removeResponse.data);
        }

        // Reset the change_permissions array after applying changes
        this.change_permissions = [];

        // Show the info dialog instead of immediately refreshing
        this.showInfoDialog = true;
      } catch (error) {
        console.error("Error applying permission changes:", error);
        alert("Failed to update permissions");
      } finally {
        this.loading = false;
      }
    },

    // Handler for when user confirms the dialog
    onDialogConfirm() {
      // Refresh the permissions list after user confirms
      this.fetchRolePermissions(this.selectedRoleId);
    },
  },
};
</script>

<style scoped>
.table {
  width: 100%;
}
</style>
